import { el } from "element-plus/es/locale";
import * as i18n from "./i18n";
import i18next from "i18next";
import { ref } from "vue";

const mappingConfig: any = {
	valid: false
};

export const localeRevision = ref(0);

const resetMapping = () => {
	mappingConfig.valid = false;
	for (const key in mappingConfig) {
		if (key !== "valid") {
			delete mappingConfig[key];
		}
	}
};

const load = () => {
	if (!mappingConfig.valid) {
		try {
			// 独立页（如 three_check）未走 i18next-vue 初始化时，i18next 可能无资源或 API 不可用
			const getData =
				i18next && typeof (i18next as any).getDataByLanguage === "function"
					? (i18next as any).getDataByLanguage.bind(i18next)
					: null;
			const data = getData?.("zh-CN")?.translation;
			if (data && typeof data === "object") {
				for (const key in data) {
					mappingConfig[String(data[key])] = key;
				}
				mappingConfig.valid = true;
			}
		} catch (e) {
			console.warn("[multilingual] 构建中英文映射失败，将直接使用原文案:", e);
		}
	}
};

if (typeof i18next?.on === "function") {
	i18next.on("languageChanged", () => {
		localeRevision.value += 1;
	});
	i18next.on("initialized", resetMapping);
	i18next.on("languageChanged", resetMapping);
}

export function toValue(t: any, key: string) {
	load();
	if (mappingConfig[key]) {
		return t(mappingConfig[key]);
	}
	return key;
}

export function toColumns(t: any, items: any[]) {
	load();
	return items.map((c: any) => {
		if (c.label) {
			c["renderHeader"] = () => {
				const key = mappingConfig[c.label];
				return key ? t(key, c.label) : c.label;
			};
		}

		if (c.dict) {
			c["dict"] = toDictWithout(c.dict);
		}

		return c;
	});
}

export function toColumnsWithout(items: any[]) {
	load();
	const t = i18n.t;
	return items.map((c: any) => {
		if (c.label) {
			c["renderHeader"] = () => {
				const chRegex = /（(.*?)）/; // 中文括号
				const enRegex = /\((.*?)\)/; // 英文括号
				const chBrackets = c.label.match(chRegex);
				const enBrackets = c.label.match(enRegex);
				let inside, outside;
				// 判断是否带单位
				if (chBrackets) {
					inside = chBrackets[1];
					// 提取中文括号外的字符
					outside = c.label.replace(/（.*?）/, "").trim();
					return isUnit(c.label, inside, outside);
				} else if (enBrackets) {
					inside = enBrackets[1];
					// 提取英文括号外的字符
					outside = c.label.replace(/\(.*?\)/, "").trim();
					return isUnit(c.label, inside, outside);
				} else {
					return t(mappingConfig[c.label], c.label)
						? t(mappingConfig[c.label], c.label)
						: c.label;
				}
			};
		}
		if (c.dict) {
			c["dict"] = toDictWithout(c.dict);
		}
		return c;
	});
}

const isUnit = (val: any, inside: any, outside: any) => {
	const t = i18n.t;
	// 再判断是否能带单位直接翻译（整句命中则直接返回）
	const full = mappingConfig[val] ? t(mappingConfig[val], val) : "";
	if (full) {
		return full;
	}
	// 拆成「名称 + 单位」时：i18n 无 key 或翻译为空时须回退中文 outside/inside，
	// 否则会出现只显示「(元)」、名称丢失（如「运费」「税额」列）
	const outT = mappingConfig[outside] ? t(mappingConfig[outside], outside) : "";
	const outsideText = outT || outside;
	const unitT = mappingConfig[inside] ? t(mappingConfig[inside], inside) : "";
	const unitText = unitT || inside;
	return outsideText + "（" + unitText + "）";
};

export function toDictWithout(items: any[]) {
	load();
	const t = i18n.t;
	return items.map((c: any) => {
		const ret = Object.assign({}, c);
		if (ret.lang_key) {
			ret.label = t(ret.lang_key, ret.label);
		} else if (ret.label) {
			ret["label"] = toValueWithout(ret.label);
		}
		return ret;
	});
}

export function toValueWithout(key: string) {
	load();
	void localeRevision.value;

	const t = i18n.t;
	// console.log("toValueWithout", mappingConfig[key])
	if (mappingConfig[key]) {
		return t(mappingConfig[key], key);
	}
	return key;
}

export function toUpsetItems(items: any[]) {
	const t = i18n.t;
	load();
	return items.map((item: any) => {
		return () => {
			// tabs
			if (
				item["type"] &&
				item["type"] == "tabs" &&
				item.props &&
				item.props.labels &&
				item.props.labels.length > 0
			) {
				item.props.labels.map((l: any) => {
					if (!l.zh_label) {
						l.zh_label = l.label;
					}
					const key = mappingConfig[l.zh_label];
					if (!key) {
						console.log("未找到对应的key: ", l.zh_label);
					} else {
						l.label = t(key, l.zh_label);
					}
					return l;
				});
				// return;
			}
			// label
			if (!item.zh_label) {
				item.zh_label = item.label;
			}
			if (item.zh_label) {
				if (t(mappingConfig[item.zh_label], item.zh_label)) {
					item.label = t(mappingConfig[item.zh_label], item.zh_label);
				}
			}
			// component;
			if (item.component) {
				// placeholder
				if (item.component.props && item.component.props.placeholder) {
					if (!item.component.props.zh_placeholder) {
						item.component.props.zh_placeholder = item.component.props.placeholder;
					}

					const key = mappingConfig[item.component.props.zh_placeholder];
					if (!key) {
						console.log(
							"未找到对应的key-placeholder: ",
							item.component.props.zh_placeholder
						);
					} else {
						item.component.props.placeholder = t(
							key,
							item.component.props.zh_placeholder
						);
					}
				}
				// options
				if (item.component.options && item.component.options.length > 0) {
					item.component.options = item.component.options.map((o: any) => {
						// if (!o.zh_label) {
						// 	o.zh_label = o.label;
						// }
						console.log("o.label", o.label);

						const key = mappingConfig[o.zh_label];
						// if (!key) {
						// 	console.log("未找到对应的key-options: ", o.zh_label);
						// } else {
						// 	o.label = t(key, o.zh_label);
						// }
						o.label = t(mappingConfig[o.label], o.label)
							? t(mappingConfig[o.label], o.label)
							: o.label;
						return o;
					});
				}
			}

			// rules;
			if (item.rules && item.rules.message) {
				// if (!item.rules.zh_message) {
				// 	item.rules.zh_message = item.rules.message;
				// }

				item.rules.message = t(mappingConfig[item.rules.message], item.rules.message)
					? t(mappingConfig[item.rules.message], item.rules.message)
					: item.rules.message;
			}
			return item;
		};
	});
}

export default {
	toValue: toValue,
	toColumns: toColumns,
	toColumnsWithout: toColumnsWithout,
	toDictWithout: toDictWithout,
	toValueWithout: toValueWithout,
	toUpsetItems: toUpsetItems
};

Object.defineProperty(String.prototype, "$t", {
	get: function $t() {
		return toValueWithout(this);
	}
});

String.prototype.t = function $t() {
	return toValueWithout(this);
};
